import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const timers = useRef({});

  useEffect(() => {
    if (!user) { setPrefs(null); return; }
    api.get("/me/preferences").then((r) => setPrefs(r.data || {})).catch(() => setPrefs({}));
  }, [user]);

  const setPref = useCallback((key, value) => {
    setPrefs((p) => ({ ...(p || {}), [key]: value }));
    clearTimeout(timers.current[key]);
    timers.current[key] = setTimeout(() => api.put("/me/preferences", { key, value }).catch(() => {}), 600);
  }, []);

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => api.post("/auth/refresh").then((r) => setUser(r.data)).catch(() => setUser(false)));
    const onLogout = () => setUser(false);
    window.addEventListener("auth:logout", onLogout);
    return () => window.removeEventListener("auth:logout", onLogout);
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
  }, []);

  const logout = useCallback(async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  }, []);

  const can = useCallback((section, level = "view") => {
    const rank = { none: 0, view: 1, edit: 2 };
    return !!user && rank[user.perms?.[section] || "none"] >= rank[level];
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, login, logout, can, isAdmin: user?.role === "admin", prefs, setPref }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
